import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { getDynamicCollectionData } from '../../../api/DynamicCollections';
import { useMessage } from '../../ContextMessage';

interface WizardSearchStep2Props {
    selectedKey: string | null;
    selectedObject: any;
    onNext: () => void;
    onAddProperty: () => void;
    propertyValues: Array<{ property: string; value: string | number; operator: string }>;
    onPropertyValueChange: (index: number, updatedProperty: { property: string; value: string | number; operator: string }) => void;
    isDynamic: boolean;
}

const WizardSearchStep2: React.FC<WizardSearchStep2Props> = ({
                                                                 selectedKey,
                                                                 selectedObject,
                                                                 onNext,
                                                                 onAddProperty,
                                                                 propertyValues,
                                                                 onPropertyValueChange,
                                                                 isDynamic,
                                                             }) => {
    const { showWarning } = useMessage();
    const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);

    // Fetch columns for dynamic collection
    useEffect(() => {
        if (isDynamic && selectedKey) {
            const fetchColumns = async () => {
                try {
                    const data = await getDynamicCollectionData(selectedKey);
                    if (data.length > 0) {
                        const sample = data[0];
                        const columns = Object.keys(sample).filter(key => !key.startsWith('_'));
                        setDynamicColumns(columns);
                    } else {
                        setDynamicColumns([]);
                        showWarning(`No data found in collection ${selectedKey}.`);
                    }
                } catch (error) {
                    console.error('Failed to fetch dynamic collection data:', error);
                    showWarning(`Failed to load columns for collection ${selectedKey}.`);
                }
            };
            fetchColumns();
        }
    }, [isDynamic, selectedKey, showWarning]);

    if (!selectedKey || !selectedObject) {
        return null;
    }

    const handlePropertyChange = (index: number, newProperty: string) => {
        const updatedPropertyValue = { ...propertyValues[index], property: newProperty };
        onPropertyValueChange(index, updatedPropertyValue);
    };

    const handleOperatorChange = (index: number, newOperator: string) => {
        const updatedPropertyValue = { ...propertyValues[index], operator: newOperator };
        onPropertyValueChange(index, updatedPropertyValue);
    };

    const handleValueChange = (index: number, newValue: string | number) => {
        const updatedPropertyValue = { ...propertyValues[index], value: newValue };
        onPropertyValueChange(index, updatedPropertyValue);
    };

    const properties = isDynamic ? dynamicColumns : Object.keys(selectedObject);

    return (
        <div>
            <br />
            <h4>Step 2: Select Properties and Values for {isDynamic ? `Dynamic: ${selectedKey}` : selectedKey}</h4>
            {propertyValues.map((property, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
                    <select
                        value={propertyValues[index].property}
                        onChange={(e) => handlePropertyChange(index, e.target.value)}
                    >
                        <option value="">Select a property</option>
                        {properties.map((prop) => (
                            <option key={prop} value={prop}>
                                {prop}
                            </option>
                        ))}
                    </select>

                    &nbsp;

                    <select
                        value={propertyValues[index].operator}
                        onChange={(e) => handleOperatorChange(index, e.target.value)}
                    >
                        <option value="==">==</option>
                        <option value="!=">!=</option>
                        <option value="<">&lt;</option>
                        <option value=">">&gt;</option>
                        <option value="REGEX">REGEX</option>
                    </select>

                    &nbsp;

                    <input
                        type="text"
                        placeholder="Enter a value"
                        value={propertyValues[index].value.toString()}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                    />
                </div>
            ))}
            <button onClick={onAddProperty} style={{ width: '140px' }}>
                <AddIcon />
                Add Property
            </button>
            <button onClick={onNext}>Execute Search</button>
        </div>
    );
};

export default WizardSearchStep2;
